import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { catalogApi } from "../../api/axios";

/**
 * Get addons
 */
export const getAddons = createAsyncThunk(
  "addon/getAddons",
  async ({ storeId }, { rejectWithValue }) => {
    try {
      const res = await catalogApi.get(
        `/v1/products/addons?store_id=${storeId}`
      );
      return res.data || [];
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Get addons group
 */
export const getAddonsGroup = createAsyncThunk(
  "addon/getAddonsGroup",
  async ({ storeId }, { rejectWithValue }) => {
    try {
      const res = await catalogApi.get(
        `/v1/products/addons/group?store_id=${storeId}`
      );
      return res.data || [];
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Get addons key/value
 */
export const getAddonsKeyValue = createAsyncThunk(
  "addon/getAddonsKeyValue",
  async ({ storeId }, { rejectWithValue }) => {
    try {
      const res = await catalogApi.get(
        `/v1/products/addons/key-value?store_id=${storeId}`
      );
      return res.data || [];
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Add addon
 */
export const addAddon = createAsyncThunk(
  "addon/addAddon",
  async (obj, { rejectWithValue }) => {
    try {
      const res = await catalogApi.post("/v1/products/addons", obj);
      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Update addon
 */
export const updateAddon = createAsyncThunk(
  "addon/updateAddon",
  async (obj, { rejectWithValue }) => {
    try {
      const res = await catalogApi.put("/v1/products/addons", obj);
      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Delete addon
 */

export const addonDelete = createAsyncThunk(
  "addon/addonDelete",
  async (obj, { rejectWithValue }) => {
    try {
      const res = await catalogApi.patch("/v1/products/addons/actions", obj);
      const dataObj = {
        ...res.data,
        ...obj,
      };
      return dataObj; // return deleted payload to update state
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Perform addon action (e.g. activate/deactivate)
 */

export const addonAction = createAsyncThunk(
  "addon/addonAction",
  async (obj, { rejectWithValue }) => {
    try {
      const res = await catalogApi.patch("/v1/products/addons/actions", obj);
      const dataObj = {
        ...res.data,
        ...obj,
      };
      return dataObj; // return updated attribute action info
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

// Slice
const addonSlice = createSlice({
  name: "addons",
  initialState: {
    list: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearAddonState: (state) => {
      state.list = [];
      state.addonGroupList = [];
      state.error = null;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // GET
      .addCase(getAddons.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAddons.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
      })
      .addCase(getAddons.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // GET ADDONS GROUP
      .addCase(getAddonsGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAddonsGroup.fulfilled, (state, action) => {
        state.loading = false;
        state.addonGroupList = action.payload;
      })
      .addCase(getAddonsGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // ADD
      .addCase(addAddon.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addAddon.fulfilled, (state, action) => {
        state.loading = false;
        state.list.push(action.payload);
      })
      .addCase(addAddon.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // UPDATE
      .addCase(updateAddon.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAddon.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        state.list = state.list.map((a) => (a.id === updated.id ? updated : a));
      })
      .addCase(updateAddon.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // DELETE
      .addCase(addonDelete.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addonDelete.fulfilled, (state, action) => {
        state.loading = false;
        const deleted = action.payload;
        state.list = state.list.filter((a) => a.uid !== deleted.uid);
      })
      .addCase(addonDelete.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // ACTION
      .addCase(addonAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addonAction.fulfilled, (state, action) => {
        state.loading = false;
        const { uid, record_status } = action.payload;
        state.list = state.list.map((a) =>
          a.uid === uid ? { ...a, record_status } : a
        );
      })
      .addCase(addonAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearAddonState } = addonSlice.actions;
export default addonSlice.reducer;
